/**
 * Payment bulk-search descriptor — adapts SearchPayments to BulkSearchResults.
 * The queried ids go in as paymentIdentifiers; status/state are server-side
 * PropertyFilter refinements.
 */
import { PropertyFilterProps } from '@cloudscape-design/components/property-filter';
import { titleCase, fullTimestamp } from 'src/store/slices/paymentDetail/paymentDetailFormat';
import { DetailLink } from 'src/components/common/DetailLink';
import {
  ApiPaymentSummary,
  PaymentFilters,
  SearchPaymentsInput,
} from 'src/store/slices/searchPayments/searchPaymentsApi';
import {
  searchPayments, searchPaymentsRegion, clearSearchPayments,
} from 'src/store/slices/searchPayments/searchPaymentsSlice';
import {
  BulkSearchArgs,
  BulkSearchDescriptor,
  StatusCell,
  dash,
  firstOf,
  joinList,
  refTypeColumns,
} from './bulkSearchShared';

const FILTERING_PROPERTIES: PropertyFilterProps.FilteringProperty[] = [
  { key: 'status', propertyLabel: 'Status', groupValuesLabel: 'Status values', operators: ['='] },
  { key: 'state', propertyLabel: 'State', groupValuesLabel: 'State values', operators: ['='] },
];

/** Columns shown by default; every other column stays toggleable in Preferences. */
const DEFAULT_VISIBLE = ['id', 'beaconStatus', 'paymentStatus', 'system', 'direction', 'paymentMethod', 'bank', 'updatedAt'];

const buildInput = ({ ids, pageNumber, pageSize, filters }: BulkSearchArgs): SearchPaymentsInput => {
  const f: PaymentFilters = { paymentIdentifiers: ids };
  const status = firstOf(filters, 'status');
  const state = firstOf(filters, 'state');
  if (status) f.beaconPaymentStatus = status;
  if (state) f.beaconPaymentState = state;
  return { filters: f, pagination: { pageNumber, pageSize } };
};

export const paymentSearchDescriptor: BulkSearchDescriptor<ApiPaymentSummary> = {
  entity: 'payment',
  title: 'Bulk payment search',
  path: '/payments',
  bulkPlaceholder: 'Enter multiple payment, artifact or execution IDs, one per line',
  filteringProperties: FILTERING_PROPERTIES,
  columns: [
    {
      id: 'id',
      header: 'End-to-end id',
      cell: (p) => <DetailLink to={`/payment?id=${encodeURIComponent(p.id)}`} newTab>{p.id}</DetailLink>,
    },
    { id: 'beaconStatus', header: 'Beacon status', cell: (p) => <StatusCell state={p.beaconPaymentState} label={p.beaconPaymentStatus} /> },
    { id: 'beaconState', header: 'Beacon state', cell: (p) => dash(p.beaconPaymentState) },
    { id: 'paymentStatus', header: 'Payment status', cell: (p) => dash(p.paymentDetails?.paymentStatus) },
    { id: 'paymentType', header: 'Payment type', cell: (p) => dash(p.paymentDetails?.paymentType) },
    { id: 'paymentMethod', header: 'Payment method', cell: (p) => dash(p.paymentDetails?.paymentMethod) },
    { id: 'bank', header: 'Bank', cell: (p) => dash(p.paymentDetails?.bank) },
    { id: 'currency', header: 'Currency', cell: (p) => dash(p.paymentDetails?.currency) },
    { id: 'pluginName', header: 'Plugin', cell: (p) => dash(p.paymentDetails?.pluginName) },
    { id: 'paymentSourceId', header: 'Payment source id', cell: (p) => dash(p.paymentDetails?.paymentSourceId) },
    { id: 'system', header: 'System', cell: (p) => dash(p.currentSystem) },
    { id: 'direction', header: 'Direction', cell: (p) => (p.currentDirection ? titleCase(p.currentDirection) : '-') },
    { id: 'systemsInvolved', header: 'Systems involved', cell: (p) => joinList(p.systemsInvolved) },
    { id: 'amazonInternalSignature', header: 'Amazon internal signature', cell: (p) => dash(p.amazonInternalSignature) },
    { id: 'disbursementId', header: 'Disbursement id', cell: (p) => dash(p.disbursementId) },
    { id: 'reissueOf', header: 'Reissue of', cell: (p) => dash(p.reissueDetails?.reissueOf) },
    { id: 'createdAt', header: 'Created', cell: (p) => (p.createdAt ? fullTimestamp(p.createdAt) : '-') },
    { id: 'updatedAt', header: 'Updated', cell: (p) => (p.updatedAt ? fullTimestamp(p.updatedAt) : '-') },
  ],
  defaultVisibleColumns: DEFAULT_VISIBLE,
  // One column per artifact ref type (PAIN001, CAMT053, …) — header = type, cell = artifactId.
  dynamicColumns: (items) => refTypeColumns(items, (p) => (p.artifacts ?? []).map((a) => ({ type: a.artifactType, id: a.artifactId }))),
  run: (dispatch, args) => { dispatch(searchPayments({ input: buildInput(args) })); },
  runRegion: (dispatch, { region, ...args }) => {
    dispatch(searchPaymentsRegion({ input: buildInput(args), region }));
  },
  clear: (dispatch) => { dispatch(clearSearchPayments()); },
  select: (s) => ({
    items: s.searchPayments.payments,
    total: s.searchPayments.total,
    loading: s.searchPayments.loading,
    error: s.searchPayments.error,
    regionResults: s.searchPayments.regionResults,
    searchedRegions: s.searchPayments.searchedRegions,
    regionErrors: s.searchPayments.regionErrors,
    loadingRegions: s.searchPayments.loadingRegions,
  }),
};
